import type { PersonNote } from "./schemas";

/**
 * Where the event behind a group of notes stands, from the author's side.
 *
 * - `member`: the author still belongs to it; the directory can be read.
 * - `not-registered`: it is still published, the author has left it.
 * - `unpublished`: it still exists (its address is reserved) but is not
 *   published, and the author is not a member.
 * - `deleted`: neither a membership, a publication nor an address is left.
 */
export type EventStatus =
  | { kind: "checking" }
  | { kind: "member"; title?: string; slug?: string }
  | { kind: "not-registered"; title?: string; slug?: string }
  | { kind: "unpublished" }
  | { kind: "deleted" };

/** What the event's directory still says about the person, when readable. */
export interface DirectoryPerson {
  name: string;
  avatar?: string;
}

export interface NoteView {
  note: PersonNote;
  /** The directory's current name, or the name the author saw. */
  name: string;
  avatar?: string;
  /**
   * True or false once the directory answered, null when it cannot be asked
   * (the author is no longer a member) or has not answered yet.
   */
  inDirectory: boolean | null;
}

export interface NoteGroup {
  eventId: string;
  title: string;
  slug?: string;
  status: EventStatus;
  notes: NoteView[];
}

const millis = (note: PersonNote): number =>
  note.updatedAt?.toMillis() ?? note.createdAt?.toMillis() ?? 0;

/**
 * Notes grouped by event, the most recently edited event first, people in
 * alphabetical order within it.
 *
 * `directory` maps a note id to the directory entry: an entry means the
 * person is listed, `null` means the directory answered and they are not
 * (withdrawn, removed or deleted), and no key means it was not asked.
 */
export function buildNoteGroups(
  notes: PersonNote[],
  statuses: ReadonlyMap<string, EventStatus>,
  directory: ReadonlyMap<string, DirectoryPerson | null>,
): NoteGroup[] {
  const byEvent = new Map<string, PersonNote[]>();
  for (const note of notes) {
    const list = byEvent.get(note.eventId) ?? [];
    list.push(note);
    byEvent.set(note.eventId, list);
  }

  const groups = [...byEvent.entries()].map(([eventId, eventNotes]) => {
    const status = statuses.get(eventId) ?? { kind: "checking" as const };
    const latestSnapshot = [...eventNotes].sort(
      (a, b) => millis(b) - millis(a),
    )[0];
    const live =
      status.kind === "member" || status.kind === "not-registered"
        ? status
        : undefined;
    const views: NoteView[] = eventNotes.map((note) => {
      const person = directory.get(note.id);
      return {
        note,
        name: person?.name || note.personName,
        avatar: person?.avatar,
        inDirectory: directory.has(note.id) ? person != null : null,
      };
    });
    views.sort((a, b) => a.name.localeCompare(b.name));
    return {
      eventId,
      title: live?.title || latestSnapshot?.eventTitle || "Untitled event",
      slug: live?.slug ?? undefined,
      status,
      notes: views,
      latest: Math.max(...eventNotes.map(millis)),
    };
  });

  groups.sort((a, b) => b.latest - a.latest);
  return groups.map(({ latest: _latest, ...group }) => group);
}

/** The event ids that need a status lookup beyond the author's memberships. */
export function eventsToResolve(
  notes: PersonNote[],
  memberEventIds: ReadonlySet<string>,
): string[] {
  return [
    ...new Set(
      notes
        .map((note) => note.eventId)
        .filter((eventId) => !memberEventIds.has(eventId)),
    ),
  ];
}
